import { gtag, initDataLayer, install } from 'ga-gtag';
import BaseService from './BaseService';
import NetworkService from "./NetworkService";
import Config from "./Config";

/**
 * Evento propio de la app, con la forma que espera GA4.
 */
interface EventData {
    category: string;
    action: string;
    label?: string;
}

/**
 * Wrapper class to manage GA
 */
class AnalyticsAPIService extends BaseService {

    // GA Tracking Id, empty if disabled.
    static trackingId: string | undefined;

    /**
     * Set consent mode defaults (Consent Mode v2).
     * analytics_storage: granted = GA can use cookies.
     * ad_*: denied = no advertising (GA4 only, no Ads).
     */
    static setConsentDefaults(): void {
        const analyticsStorage = Config.isAnalyticsCookiesEnabled() ? 'granted' : 'denied';
        gtag('consent', 'default', {
            'analytics_storage': analyticsStorage,
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied'
        });
    }

    /**
     * Init React-ga lib.
     */
    static init(): void {
        if (!Config.isAnalyticsEnabled())
            return;

        this.debug("AnalyticsAPIService.init()");
        this.trackingId = this.config().google_analytics_tracking_id;

        if (this.trackingId) {
            initDataLayer();
            this.setConsentDefaults();
            install(this.trackingId);
        }

    }

    /**
     * Check whether GA service is enabled.
     */
    static isEnabled(): boolean {
      return Config.isAnalyticsEnabled() && !!this.trackingId;
    }

    /**
     * Send event to GA
     *
     * @param label Optional event label
     */
    static sendEvent(category: string, action: string, label?: string): void {
      if (!this.isEnabled() || NetworkService.isOffline()) {
          return;
      }

      const data: EventData = {
         category,
         action
      };
      if (label) {
        data.label = label;
      }
      if (this.trackingId) {
          this.debug('AnalyticsAPIService.sendEvent', data);
          // El nombre del evento es la accion, igual que en sendCustomEvent.
          gtag('event', action, data);
      }
    }


    /**
     * Send url/path.
     */
    static async sendPageView(url: string): Promise<void> {
      if (!this.isEnabled() || NetworkService.isOffline()) {
          return;
      }

      if (this.trackingId) {
        this.debug('AnalyticsAPIService.sendPageView', url);
        gtag('event', 'page_view', { page: url });
      }
    }

    /**
     * Send event about outbound link.
     *
     * @param action Event action
     * @param label Event label
     * @param page Event page
     */
    static async sendCustomEvent(action: string, label: string, page: string): Promise<void> {
        if (!this.isEnabled() || NetworkService.isOffline()) {
            return;
        }

        const data = {
            action,
            page_title: label,
            page_location: page,
        };
        if (this.trackingId) {
            this.debug('AnalyticsAPIService.sendEvent', data);
            gtag('event', action, data);
        }
    }

    /**
     * Send event about a modal page view (card)
     *
     * @param cardTitle Title of the card
     * @param url Url of the content loaded in the card
     */
    static async sendViewCard(cardTitle: string, url: string): Promise<void> {
        await this.sendCustomEvent(
            'screen_view',
            cardTitle + ' | Gronze.maps',
            url
        );
    }

    /**
     * Send event about outbound link.
     *
     * @param label Label of the link
     * @param url URL opened in external browser
     */
    static async sendOutboundLink(label: string, url: string): Promise<void> {
        await this.sendCustomEvent(
            'page_view',
            label + ' | Gronze.maps',
            url
        );
    }
}

export default AnalyticsAPIService;
